/**
 * EditPromptModal Component (UI-2)
 * Modal for creating and editing Q AI prompts
 *
 * Features:
 * - Read-only fields: prompt_key, category (on edit)
 * - Editable fields: promptName, description, promptText, isActive
 * - Character counter (max 10,000)
 * - Variable chips section (clickable to insert)
 * - Change reason field (optional)
 * - "Test Before Save" button
 */

import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Chip,
    Alert,
    IconButton,
    Tooltip,
    Collapse,
    LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScienceIcon from '@mui/icons-material/Science';
import PreviewIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';

import {
    createPrompt,
    updatePrompt,
    previewPrompt,
    PROMPT_CATEGORIES,
    PROMPT_VARIABLES,
    PROMPT_LIMITS,
    PROMPT_KEY_REGEX,
    METADATA_TIERS,
    METADATA_DOMAINS,
    METADATA_INTENTS,
    METADATA_PRIORITIES,
    METADATA_LOAD_CONDITIONS,
    DEFAULT_METADATA
} from '../../api/promptsApi';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    borderRadius: { sm: '8px', md: '12px' }
};

// Group variables by category
const groupedVariables = PROMPT_VARIABLES.reduce((acc, v) => {
    if (!acc[v.category]) acc[v.category] = [];
    acc[v.category].push(v);
    return acc;
}, {});

const EditPromptModal = ({ open, prompt, isCreating, onClose, onSuccess }) => {
    // Form state
    const [formData, setFormData] = useState({
        promptKey: '',
        promptName: '',
        promptText: '',
        description: '',
        category: 'system',
        supportsVariables: true,
        isActive: true,
        changeReason: '',
        // Metadata fields (v4 RAG)
        metadata: { ...DEFAULT_METADATA }
    });

    // UI state for metadata section
    const [metadataExpanded, setMetadataExpanded] = useState(false);
    const [tagsInput, setTagsInput] = useState('');

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [variablesExpanded, setVariablesExpanded] = useState(true);
    const [textExpanded, setTextExpanded] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Preview state
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [previewExpanded, setPreviewExpanded] = useState(false);

    // Ref for textarea cursor position
    const textAreaRef = useRef(null);

    // ─────────────────────────────────────────────────────────────────────────
    // Initialize form data when prompt changes
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (prompt && !isCreating) {
            setFormData({
                promptKey: prompt.promptKey || '',
                promptName: prompt.promptName || '',
                promptText: prompt.promptText || '',
                description: prompt.description || '',
                category: prompt.category || 'system',
                supportsVariables: prompt.supportsVariables ?? true,
                isActive: prompt.isActive ?? true,
                changeReason: '',
                metadata: prompt.metadata ? { ...DEFAULT_METADATA, ...prompt.metadata } : { ...DEFAULT_METADATA }
            });
            setTagsInput((prompt.metadata?.tags || []).join(', '));
            // Expand metadata section if prompt has tier 0-2 (important prompts)
            setMetadataExpanded(prompt.metadata?.tier !== undefined && prompt.metadata.tier <= 2);
        } else if (isCreating) {
            setFormData({
                promptKey: '',
                promptName: '',
                promptText: '',
                description: '',
                category: 'system',
                supportsVariables: true,
                isActive: true,
                changeReason: '',
                metadata: { ...DEFAULT_METADATA }
            });
            setTagsInput('');
            setMetadataExpanded(false);
        }
        setError(null);
        setValidationErrors({});
        setPreviewResult(null);
        setPreviewExpanded(false);
    }, [prompt, isCreating, open]);

    // ─────────────────────────────────────────────────────────────────────────
    // Form handlers
    // ─────────────────────────────────────────────────────────────────────────

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    // Handler for metadata fields
    const handleMetadataChange = (field) => (event) => {
        const value = event.target.type === 'number' ? parseInt(event.target.value, 10) : event.target.value;
        setFormData(prev => ({
            ...prev,
            metadata: { ...prev.metadata, [field]: value }
        }));
    };

    // Handler for tags input
    const handleTagsChange = (event) => {
        setTagsInput(event.target.value);
        // Parse comma-separated tags
        const tags = event.target.value
            .split(',')
            .map(t => t.trim().toLowerCase())
            .filter(t => t.length > 0);
        setFormData(prev => ({
            ...prev,
            metadata: { ...prev.metadata, tags }
        }));
    };

    const handleVariableClick = (variable) => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData.promptText;
        const newText = text.substring(0, start) + variable.key + text.substring(end);

        setFormData(prev => ({ ...prev, promptText: newText }));

        // Set cursor position after insert
        setTimeout(() => {
            textarea.focus();
            const newPos = start + variable.key.length;
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const copyVariable = (variable) => {
        navigator.clipboard.writeText(variable.key);
        toast.success(`Copied ${variable.key}`);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Preview with Variables
    // ─────────────────────────────────────────────────────────────────────────

    const handlePreview = async () => {
        // For new prompts without a key yet, show a local preview
        if (isCreating || !formData.promptKey) {
            // Just show the raw text with sample variable values
            const sampleVariables = {
                '{{user.tokenBalance}}': '500',
                '{{user.tokensUsedTotal}}': '1,234',
                '{{sync.tradeCount}}': '42',
                '{{sync.lastSyncAt}}': new Date().toLocaleDateString(),
                '{{sync.broker}}': 'MetaTrader 5 Demo',
                '{{signal.symbol}}': 'EUR/USD',
                '{{signal.qSignal}}': 'BUY',
                '{{signal.qScore}}': '78'
            };

            let previewText = formData.promptText;
            Object.entries(sampleVariables).forEach(([key, value]) => {
                previewText = previewText.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
            });

            setPreviewResult({
                rendered: previewText,
                variablesUsed: Object.keys(sampleVariables).filter(k => formData.promptText.includes(k))
            });
            setPreviewExpanded(true);
            return;
        }

        // For existing prompts, call the API
        setPreviewLoading(true);
        setError(null);

        const sampleVariables = {
            'user.tokenBalance': 500,
            'user.tokensUsedTotal': 1234,
            'sync.tradeCount': 42,
            'sync.lastSyncAt': new Date().toISOString(),
            'sync.broker': 'MetaTrader 5 Demo',
            'signal.symbol': 'EUR/USD',
            'signal.qSignal': 'BUY',
            'signal.qScore': 78
        };

        const result = await previewPrompt(formData.promptKey, sampleVariables);
        setPreviewLoading(false);

        if (result.success) {
            setPreviewResult(result.data);
            setPreviewExpanded(true);
        } else {
            // Fallback to local preview if API fails
            toast.warning('API preview unavailable, showing local preview');
            let previewText = formData.promptText;
            PROMPT_VARIABLES.forEach(v => {
                const value = sampleVariables[v.key.replace(/[{}]/g, '')] || v.key;
                previewText = previewText.replace(new RegExp(v.key.replace(/[{}]/g, '\\$&'), 'g'), String(value));
            });
            setPreviewResult({ rendered: previewText, variablesUsed: [] });
            setPreviewExpanded(true);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Validation
    // ─────────────────────────────────────────────────────────────────────────

    const validate = () => {
        const errors = {};

        if (isCreating) {
            if (!formData.promptKey.trim()) {
                errors.promptKey = 'Prompt key is required';
            } else if (!PROMPT_KEY_REGEX.test(formData.promptKey)) {
                errors.promptKey = 'Key must start with letter, contain only lowercase letters, numbers, and underscores';
            } else if (formData.promptKey.length > PROMPT_LIMITS.promptKey) {
                errors.promptKey = `Key must be ${PROMPT_LIMITS.promptKey} characters or less`;
            }
        }

        if (!formData.promptName.trim()) {
            errors.promptName = 'Display name is required';
        } else if (formData.promptName.length > PROMPT_LIMITS.promptName) {
            errors.promptName = `Name must be ${PROMPT_LIMITS.promptName} characters or less`;
        }

        if (!formData.promptText.trim()) {
            errors.promptText = 'Prompt text is required';
        } else if (formData.promptText.length > PROMPT_LIMITS.promptText) {
            errors.promptText = `Text must be ${PROMPT_LIMITS.promptText.toLocaleString()} characters or less`;
        }

        if (formData.description && formData.description.length > PROMPT_LIMITS.description) {
            errors.description = `Description must be ${PROMPT_LIMITS.description} characters or less`;
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Submit
    // ─────────────────────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setError(null);

        const payload = {
            promptName: formData.promptName.trim(),
            promptText: formData.promptText,
            description: formData.description.trim(),
            isActive: formData.isActive,
            changeReason: formData.changeReason.trim(),
            // Include metadata (v4 RAG)
            metadata: {
                tier: formData.metadata.tier,
                domain: formData.metadata.domain,
                intent: formData.metadata.intent,
                priority: formData.metadata.priority,
                loadCondition: formData.metadata.loadCondition,
                tokenBudget: formData.metadata.tokenBudget || Math.ceil(formData.promptText.length / 4),
                tags: formData.metadata.tags || []
            }
        };

        if (isCreating) {
            payload.promptKey = formData.promptKey.trim();
            payload.category = formData.category;
            payload.supportsVariables = formData.supportsVariables;
        }

        const result = isCreating
            ? await createPrompt(payload)
            : await updatePrompt(formData.promptKey, payload);

        setLoading(false);

        if (result.success) {
            onSuccess?.();
        } else {
            setError(result.error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Text field styles
    // ─────────────────────────────────────────────────────────────────────────

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            background: theme.colors.bg.tertiary,
            color: theme.colors.text.primary,
            '& fieldset': { borderColor: theme.colors.border.primary },
            '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
            '&.Mui-focused fieldset': { borderColor: theme.colors.accent.primary }
        },
        '& .MuiInputLabel-root': { color: theme.colors.text.quaternary },
        '& .MuiInputLabel-root.Mui-focused': { color: theme.colors.accent.tertiary },
        '& .MuiInputBase-input::placeholder': { color: theme.colors.text.placeholder }
    };

    const charCount = formData.promptText.length;
    const charCountColor = charCount > PROMPT_LIMITS.promptText
        ? theme.colors.status.error
        : charCount > PROMPT_LIMITS.promptText * 0.9
            ? theme.colors.status.warning
            : theme.colors.text.quaternary;

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.border.primary}`
                }
            }}
        >
            {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}

            {/* Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.colors.border.primary}`,
                    pb: 2
                }}
            >
                <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '1.25rem' }}>
                    {isCreating ? 'Create New Prompt' : `Edit: ${prompt?.promptName || ''}`}
                </Typography>
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    sx={{ color: theme.colors.text.quaternary }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ pt: 3 }}>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2, background: `${theme.colors.status.error}20`, color: theme.colors.status.error }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Row 1: Key & Category (read-only on edit) */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Prompt Key"
                            value={formData.promptKey}
                            onChange={handleChange('promptKey')}
                            disabled={!isCreating}
                            error={!!validationErrors.promptKey}
                            helperText={validationErrors.promptKey || (isCreating ? 'Unique identifier (e.g., system_intro)' : 'Read-only')}
                            fullWidth
                            sx={textFieldSx}
                            inputProps={{ style: { fontFamily: 'monospace' } }}
                        />
                        <FormControl fullWidth sx={textFieldSx}>
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Category</InputLabel>
                            <Select
                                value={formData.category}
                                onChange={handleChange('category')}
                                label="Category"
                                disabled={!isCreating}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                }}
                            >
                                {PROMPT_CATEGORIES.map(cat => (
                                    <MenuItem key={cat.value} value={cat.value}>
                                        {cat.label} - {cat.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Row 2: Display Name */}
                    <TextField
                        label="Display Name"
                        value={formData.promptName}
                        onChange={handleChange('promptName')}
                        error={!!validationErrors.promptName}
                        helperText={validationErrors.promptName}
                        fullWidth
                        required
                        sx={textFieldSx}
                    />

                    {/* Row 3: Description */}
                    <TextField
                        label="Description"
                        value={formData.description}
                        onChange={handleChange('description')}
                        error={!!validationErrors.description}
                        helperText={validationErrors.description || 'Brief description of what this prompt does'}
                        fullWidth
                        sx={textFieldSx}
                    />

                    {/* Row 4: Prompt Text */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                                Prompt Text *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ color: charCountColor, fontSize: '0.75rem' }}>
                                    {charCount.toLocaleString()} / {PROMPT_LIMITS.promptText.toLocaleString()}
                                </Typography>
                                <Tooltip title={textExpanded ? 'Collapse' : 'Expand'}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setTextExpanded(!textExpanded)}
                                        sx={{ color: theme.colors.text.quaternary }}
                                    >
                                        {textExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <TextField
                            inputRef={textAreaRef}
                            value={formData.promptText}
                            onChange={handleChange('promptText')}
                            error={!!validationErrors.promptText}
                            helperText={validationErrors.promptText}
                            multiline
                            rows={textExpanded ? 15 : 6}
                            fullWidth
                            placeholder="Enter the prompt text here..."
                            sx={{
                                ...textFieldSx,
                                '& .MuiOutlinedInput-root': {
                                    ...textFieldSx['& .MuiOutlinedInput-root'],
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    </Box>

                    {/* Row 5: Available Variables */}
                    {formData.supportsVariables && (
                        <Box
                            sx={{
                                border: `1px solid ${theme.colors.border.primary}`,
                                borderRadius: theme.borderRadius.sm,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    background: theme.colors.bg.tertiary,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setVariablesExpanded(!variablesExpanded)}
                            >
                                <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                                    Available Variables (click to insert)
                                </Typography>
                                <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>
                                    {variablesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                            <Collapse in={variablesExpanded}>
                                <Box sx={{ p: 1.5 }}>
                                    {Object.entries(groupedVariables).map(([category, variables]) => (
                                        <Box key={category} sx={{ mb: 1.5 }}>
                                            <Typography
                                                sx={{
                                                    color: theme.colors.text.quaternary,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    mb: 0.5
                                                }}
                                            >
                                                {category}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {variables.map(v => (
                                                    <Tooltip key={v.key} title={v.description}>
                                                        <Chip
                                                            label={v.key}
                                                            size="small"
                                                            onClick={() => handleVariableClick(v)}
                                                            onDelete={() => copyVariable(v)}
                                                            deleteIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                                                            sx={{
                                                                background: theme.colors.bg.quaternary,
                                                                color: theme.colors.accent.tertiary,
                                                                fontFamily: 'monospace',
                                                                fontSize: '0.75rem',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    background: theme.colors.accent.primary + '30'
                                                                },
                                                                '& .MuiChip-deleteIcon': {
                                                                    color: theme.colors.text.quaternary,
                                                                    '&:hover': { color: theme.colors.accent.tertiary }
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {/* Row 5.5: Preview with Variables Button & Result */}
                    {formData.supportsVariables && formData.promptText.trim() && (
                        <Box>
                            <Button
                                variant="outlined"
                                startIcon={previewLoading ? null : <PreviewIcon />}
                                onClick={handlePreview}
                                disabled={previewLoading || !formData.promptText.trim()}
                                sx={{
                                    borderColor: theme.colors.status.info,
                                    color: theme.colors.status.info,
                                    '&:hover': {
                                        borderColor: theme.colors.status.info,
                                        background: `${theme.colors.status.info}10`
                                    }
                                }}
                            >
                                {previewLoading ? 'Loading...' : 'Preview with Sample Variables'}
                            </Button>

                            {/* Preview Result Panel */}
                            <Collapse in={previewExpanded && previewResult}>
                                <Box
                                    sx={{
                                        mt: 2,
                                        border: `1px solid ${theme.colors.status.info}40`,
                                        borderRadius: theme.borderRadius.sm,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 1.5,
                                            background: `${theme.colors.status.info}15`,
                                            borderBottom: `1px solid ${theme.colors.status.info}30`
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PreviewIcon sx={{ color: theme.colors.status.info, fontSize: 18 }} />
                                            <Typography sx={{ color: theme.colors.status.info, fontWeight: 600, fontSize: '0.85rem' }}>
                                                Preview (with sample values)
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => setPreviewExpanded(false)}
                                            sx={{ color: theme.colors.text.quaternary }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Box
                                        sx={{
                                            p: 2,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            background: theme.colors.bg.tertiary,
                                            fontFamily: 'monospace',
                                            fontSize: '0.8rem',
                                            color: theme.colors.text.tertiary,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {previewResult?.rendered || 'No preview available'}
                                    </Box>
                                    {previewResult?.variablesUsed?.length > 0 && (
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderTop: `1px solid ${theme.colors.border.primary}`,
                                                background: theme.colors.bg.quaternary
                                            }}
                                        >
                                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.7rem', mb: 0.5 }}>
                                                Variables replaced:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {previewResult.variablesUsed.map((v, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={v}
                                                        size="small"
                                                        sx={{
                                                            background: `${theme.colors.status.success}20`,
                                                            color: theme.colors.status.success,
                                                            fontSize: '0.7rem',
                                                            height: 20
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {/* Row 6: RAG Metadata Section (v4) */}
                    <Box
                        sx={{
                            border: `1px solid ${theme.colors.border.primary}`,
                            borderRadius: theme.borderRadius.sm,
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                background: formData.metadata?.tier <= 1
                                    ? `${theme.colors.status.warning}15`
                                    : theme.colors.bg.tertiary,
                                cursor: 'pointer'
                            }}
                            onClick={() => setMetadataExpanded(!metadataExpanded)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                                    RAG Metadata (v4)
                                </Typography>
                                {formData.metadata?.tier !== undefined && (
                                    <Chip
                                        label={`Tier ${formData.metadata.tier}`}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            background: formData.metadata.tier === 0
                                                ? `${theme.colors.status.error}30`
                                                : formData.metadata.tier === 1
                                                    ? `${theme.colors.status.warning}30`
                                                    : `${theme.colors.accent.primary}30`,
                                            color: formData.metadata.tier === 0
                                                ? theme.colors.status.error
                                                : formData.metadata.tier === 1
                                                    ? theme.colors.status.warning
                                                    : theme.colors.accent.tertiary
                                        }}
                                    />
                                )}
                            </Box>
                            <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>
                                {metadataExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                        <Collapse in={metadataExpanded}>
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Row 1: Tier & Priority */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl fullWidth sx={textFieldSx}>
                                        <InputLabel sx={{ color: theme.colors.text.quaternary }}>Load Tier</InputLabel>
                                        <Select
                                            value={formData.metadata?.tier ?? 3}
                                            onChange={handleMetadataChange('tier')}
                                            label="Load Tier"
                                            sx={{
                                                background: theme.colors.bg.tertiary,
                                                color: theme.colors.text.primary,
                                                '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                            }}
                                        >
                                            {METADATA_TIERS.map(t => (
                                                <MenuItem key={t.value} value={t.value}>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 500 }}>{t.label}</Typography>
                                                        <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.quaternary }}>
                                                            {t.description}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth sx={textFieldSx}>
                                        <InputLabel sx={{ color: theme.colors.text.quaternary }}>Priority</InputLabel>
                                        <Select
                                            value={formData.metadata?.priority || 'medium'}
                                            onChange={handleMetadataChange('priority')}
                                            label="Priority"
                                            sx={{
                                                background: theme.colors.bg.tertiary,
                                                color: theme.colors.text.primary,
                                                '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                            }}
                                        >
                                            {METADATA_PRIORITIES.map(p => (
                                                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Row 2: Domain & Intent */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl fullWidth sx={textFieldSx}>
                                        <InputLabel sx={{ color: theme.colors.text.quaternary }}>Domain</InputLabel>
                                        <Select
                                            value={formData.metadata?.domain || 'system'}
                                            onChange={handleMetadataChange('domain')}
                                            label="Domain"
                                            sx={{
                                                background: theme.colors.bg.tertiary,
                                                color: theme.colors.text.primary,
                                                '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                            }}
                                        >
                                            {METADATA_DOMAINS.map(d => (
                                                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth sx={textFieldSx}>
                                        <InputLabel sx={{ color: theme.colors.text.quaternary }}>Intent</InputLabel>
                                        <Select
                                            value={formData.metadata?.intent || 'analysis'}
                                            onChange={handleMetadataChange('intent')}
                                            label="Intent"
                                            sx={{
                                                background: theme.colors.bg.tertiary,
                                                color: theme.colors.text.primary,
                                                '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                            }}
                                        >
                                            {METADATA_INTENTS.map(i => (
                                                <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Row 3: Load Condition & Token Budget */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl fullWidth sx={textFieldSx}>
                                        <InputLabel sx={{ color: theme.colors.text.quaternary }}>Load Condition</InputLabel>
                                        <Select
                                            value={formData.metadata?.loadCondition || 'always'}
                                            onChange={handleMetadataChange('loadCondition')}
                                            label="Load Condition"
                                            sx={{
                                                background: theme.colors.bg.tertiary,
                                                color: theme.colors.text.primary,
                                                '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                                            }}
                                        >
                                            {METADATA_LOAD_CONDITIONS.map(c => (
                                                <MenuItem key={c.value} value={c.value}>
                                                    <Box>
                                                        <Typography>{c.label}</Typography>
                                                        <Typography sx={{ fontSize: '0.7rem', color: theme.colors.text.quaternary }}>
                                                            {c.description}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        type="number"
                                        label="Token Budget"
                                        value={formData.metadata?.tokenBudget || 200}
                                        onChange={handleMetadataChange('tokenBudget')}
                                        helperText={`Auto-estimate: ~${Math.ceil(formData.promptText.length / 4)} tokens`}
                                        fullWidth
                                        sx={textFieldSx}
                                        inputProps={{ min: 0, max: 5000 }}
                                    />
                                </Box>

                                {/* Row 4: Tags */}
                                <TextField
                                    label="Tags (comma-separated)"
                                    value={tagsInput}
                                    onChange={handleTagsChange}
                                    placeholder="identity, security, tier0, lockdown"
                                    helperText="Searchable tags for filtering prompts"
                                    fullWidth
                                    sx={textFieldSx}
                                />
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Row 7: Change Reason (only for updates) */}
                    {!isCreating && (
                        <TextField
                            label="Change Reason (optional)"
                            value={formData.changeReason}
                            onChange={handleChange('changeReason')}
                            placeholder="Describe what you changed and why..."
                            fullWidth
                            sx={textFieldSx}
                        />
                    )}

                    {/* Row 7: Status Toggle */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={handleChange('isActive')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: theme.colors.status.success
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: theme.colors.status.success
                                    }
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ color: theme.colors.text.secondary }}>
                                Active - {formData.isActive ? 'This prompt will be used by Q AI' : 'This prompt is disabled'}
                            </Typography>
                        }
                    />
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                sx={{
                    borderTop: `1px solid ${theme.colors.border.primary}`,
                    p: 2,
                    gap: 1
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={loading}
                    sx={{ color: theme.colors.text.tertiary }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        background: theme.colors.accent.primary,
                        '&:hover': { background: theme.colors.accent.secondary }
                    }}
                >
                    {loading ? 'Saving...' : (isCreating ? 'Create Prompt' : 'Save Changes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditPromptModal;
